import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Send, Bot, ShieldCheck, Globe } from 'lucide-react';
type AiMsg = {
  id: string;
  from: 'ai' | 'supplier' | 'customer';
  text: string;
  delivered?: boolean;
};
type LangCode = 'en' | 'ar' | 'ur' | 'hi';
const LANGUAGES: Array<{
  code: LangCode;
  flag: string;
  label: string;
  native: string;
}> = [{
  code: 'en',
  flag: '🇬🇧',
  label: 'English',
  native: 'English'
}, {
  code: 'ar',
  flag: '🇦🇪',
  label: 'Arabic',
  native: 'العربية'
}, {
  code: 'ur',
  flag: '🇵🇰',
  label: 'Urdu',
  native: 'اردو'
}, {
  code: 'hi',
  flag: '🇮🇳',
  label: 'Hindi',
  native: 'हिन्दी'
}];
const LANG_BY_CODE: Record<LangCode, (typeof LANGUAGES)[number]> = Object.fromEntries(LANGUAGES.map(l => [l.code, l])) as Record<LangCode, (typeof LANGUAGES)[number]>;
const SAMPLE_REPLY_BY_LANG: Record<LangCode, string> = {
  en: "Thanks for checking. I'll get back to you on this shortly — let me know if you need anything else.",
  ar: 'شكرًا على التواصل. سأرد عليك قريبًا — أخبرني إن كنت بحاجة إلى أي شيء آخر.',
  ur: 'پوچھنے کا شکریہ۔ میں جلد ہی آپ سے رابطہ کروں گا — اگر کوئی اور بات ہو تو بتائیے۔',
  hi: 'पूछने के लिए धन्यवाद। मैं जल्द ही आपको जवाब दूंगा — कुछ और चाहिए तो बताइए।'
};
export type AskCustomerAiSliderProps = {
  open: boolean;
  customerName: string;
  /** Short context shown under the header, e.g. job id or request id */
  contextLabel?: string;
  /** Optional contextual prompts shown above the input */
  quickPrompts?: string[];
  onClose: () => void;
};
const DEFAULT_PROMPTS = ['Confirm the building has lift access?', 'Is anyone home for the pickup?', 'Can pickup be moved 1 hour later?', 'Any narrow streets or access restrictions?'];
function synthesizeCustomerReply(question: string, lang: LangCode = 'en'): string {
  // Non-English customers reply in their own language with a generic note.
  if (lang !== 'en') return SAMPLE_REPLY_BY_LANG[lang];
  const q = question.toLowerCase();
  if (q.includes('lift') || q.includes('elevator')) {
    return 'Yes — the building has a service lift on the basement level. Booked it for the move window.';
  }
  if (q.includes('time') || q.includes('hour') || q.includes('later') || q.includes('earlier') || q.includes('eta')) {
    return 'Sure, an hour later works. I have a meeting until 10:30 so anything after 11 is fine.';
  }
  if (q.includes('home') || q.includes('someone') || q.includes('anyone')) {
    return "Yes, my brother will be there to let the team in. I'll share his number once the job is confirmed.";
  }
  if (q.includes('access') || q.includes('street') || q.includes('parking')) {
    return "Parking is tight in front. There's a loading bay around the back you can use — security has been informed.";
  }
  if (q.includes('photo') || q.includes('picture') || q.includes('image')) {
    return "I can send a couple more pictures of the larger items — let me know what specifically you'd like to see.";
  }
  if (q.includes('price') || q.includes('cost') || q.includes('quote')) {
    return "I'm flexible on price if you can guarantee the timing. Send me a final quote and I'll decide today.";
  }
  if (q.includes('address') || q.includes('location') || q.includes('where')) {
    return 'The exact pin is on the booking — entrance is on the side street, not the main road. Look for the blue gate.';
  }
  return "Thanks for checking. I'll get back to you on this shortly — let me know if you need anything else.";
}
export function AskCustomerAiSlider({
  open,
  customerName,
  contextLabel,
  quickPrompts = DEFAULT_PROMPTS,
  onClose
}: AskCustomerAiSliderProps) {
  const [messages, setMessages] = useState<AiMsg[]>([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [language, setLanguage] = useState<LangCode>('en');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Reset thread whenever we open against a new customer/context
  useEffect(() => {
    if (open) {
      setLanguage('en');
      setMessages([{
        id: 'ai-1',
        from: 'ai',
        text: `Hi — I'll relay your questions to ${customerName} on your behalf. You can type in any language (English, Arabic, Urdu, or Hindi). By default I'll contact ${customerName} in English — switch the language above anytime if they'd prefer another. What would you like me to ask?`
      }]);
      setInput('');
      setThinking(false);
    }
  }, [open, customerName, contextLabel]);
  const handleLanguageChange = (code: LangCode) => {
    if (code === language) return;
    setLanguage(code);
    const lang = LANG_BY_CODE[code];
    setMessages(m => [...m, {
      id: `ai-lang-${Date.now()}`,
      from: 'ai',
      text: `Got it — I'll relay messages to ${customerName} in ${lang.label} (${lang.native}) from now on.`
    }]);
  };
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, thinking]);
  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  const sendQuestion = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setInput('');
    setMessages(m => [...m, {
      id: `s-${Date.now()}`,
      from: 'supplier',
      text: trimmed
    }]);
    setThinking(true);
    setTimeout(() => {
      setMessages(m => [...m, {
        id: `ai-${Date.now()}`,
        from: 'ai',
        text: `I've relayed your question to ${customerName}. Waiting for their reply…`,
        delivered: true
      }]);
    }, 600);
    setTimeout(() => {
      setMessages(m => [...m, {
        id: `c-${Date.now()}`,
        from: 'customer',
        text: synthesizeCustomerReply(trimmed, language)
      }]);
      setThinking(false);
    }, 2000);
  };
  return <AnimatePresence data-id="element-145">
      {open && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" data-id="element-146" />

          {/* Right-side full-height drawer */}
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 34
      }} className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[440px] md:w-[480px] bg-white shadow-2xl flex flex-col" role="dialog" aria-label="Ask customer via AI" data-id="element-147">
            {/* Header */}
            <div className="px-5 py-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white flex items-center justify-between shrink-0" data-id="element-148">
              <div className="flex items-center gap-3 min-w-0" data-id="element-149">
                <div className="relative shrink-0" data-id="element-150">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center shadow-lg" data-id="element-151">
                    <Sparkles className="w-5 h-5 text-white" data-id="element-152" />
                  </div>
                  <motion.div animate={{
                scale: [1, 1.2, 1],
                opacity: [0.6, 0.2, 0.6]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} className="absolute inset-0 rounded-xl ring-2 ring-brand-400/40" data-id="element-153" />
                </div>
                <div className="min-w-0" data-id="element-154">
                  <h2 className="font-display font-bold text-base" data-id="element-155">
                    Pair2 AI Liaison
                  </h2>
                  <p className="text-[11px] text-gray-300 truncate" data-id="element-156">
                    Relaying to {customerName}
                    {contextLabel ? ` · ${contextLabel}` : ''}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors shrink-0" aria-label="Close" data-id="element-157">
                <X className="w-4 h-4" data-id="element-158" />
              </button>
            </div>

            {/* Privacy note */}
            <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100 flex items-center gap-2 shrink-0" data-id="element-159">
              <ShieldCheck className="w-3.5 h-3.5 text-amber-700 shrink-0" data-id="element-160" />
              <p className="text-[11px] text-amber-800 leading-snug" data-id="element-161">
                Direct customer contact is unavailable pre-job. The AI rewrites
                your message and shares only what's needed.
              </p>
            </div>

            {/* Language selector */}
            <div className="px-5 py-3 border-b border-gray-100 bg-white shrink-0" data-id="element-162">
              <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-163">
                <Globe className="w-3 h-3" data-id="element-164" />
                Contact language
                <span className="font-medium text-gray-400 normal-case tracking-normal ml-1" data-id="element-165">
                  · type in any of these, default is English
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-166">
                {LANGUAGES.map(l => {
              const isActive = l.code === language;
              return <button key={l.code} onClick={() => handleLanguageChange(l.code)} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition-all ${isActive ? 'bg-gray-900 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} aria-pressed={isActive} data-id="element-167">
                      <span className="text-sm leading-none" data-id="element-168">{l.flag}</span>
                      <span data-id="element-169">{l.label}</span>
                      <span className={`font-normal ${isActive ? 'text-white/70' : 'text-gray-400'}`} data-id="element-170">
                        {l.native}
                      </span>
                    </button>;
            })}
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-50" data-id="element-171">
              <AnimatePresence initial={false} data-id="element-172">
                {messages.map(m => {
              if (m.from === 'ai') {
                return <motion.div key={m.id} initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="flex gap-2 max-w-[90%]" data-id="element-173">
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center shrink-0" data-id="element-174">
                          <Sparkles className="w-3.5 h-3.5 text-white" data-id="element-175" />
                        </div>
                        <div data-id="element-176">
                          <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3.5 py-2" data-id="element-177">
                            <p className="text-sm text-gray-800 leading-relaxed" data-id="element-178">
                              {m.text}
                            </p>
                          </div>
                          <p className="text-[10px] text-gray-400 mt-1 ml-1" data-id="element-179">
                            Pair2 AI
                          </p>
                        </div>
                      </motion.div>;
              }
              if (m.from === 'supplier') {
                return <motion.div key={m.id} initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="flex justify-end" data-id="element-180">
                        <div className="max-w-[85%]" data-id="element-181">
                          <div className="bg-gray-900 text-white rounded-2xl rounded-tr-sm px-3.5 py-2" data-id="element-182">
                            <p className="text-sm leading-relaxed" data-id="element-183">{m.text}</p>
                          </div>
                          <p className="text-[10px] text-gray-400 mt-1 mr-1 text-right" data-id="element-184">
                            You → AI
                          </p>
                        </div>
                      </motion.div>;
              }
              return <motion.div key={m.id} initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex gap-2 max-w-[90%]" data-id="element-185">
                      <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-xs shrink-0" data-id="element-186">
                        {customerName.charAt(0)}
                      </div>
                      <div data-id="element-187">
                        <div className="bg-brand-50 border border-brand-100 rounded-2xl rounded-tl-sm px-3.5 py-2" data-id="element-188">
                          <p className="text-sm text-gray-800 leading-relaxed" data-id="element-189">
                            {m.text}
                          </p>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1 ml-1" data-id="element-190">
                          {customerName} · via AI
                        </p>
                      </div>
                    </motion.div>;
            })}
                {thinking && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="flex gap-2" data-id="element-191">
                    <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-xs shrink-0" data-id="element-192">
                      {customerName.charAt(0)}
                    </div>
                    <div className="bg-brand-50 border border-brand-100 rounded-2xl rounded-tl-sm px-4 py-2.5 flex items-center gap-1" data-id="element-193">
                      {[0, 0.2, 0.4].map((d, i) => <motion.span key={i} animate={{
                  opacity: [0.3, 1, 0.3]
                }} transition={{
                  duration: 1.2,
                  repeat: Infinity,
                  delay: d
                }} className="w-1.5 h-1.5 rounded-full bg-brand-500" data-id="element-194" />)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Quick prompts */}
            <div className="px-5 py-2.5 border-t border-gray-100 bg-white shrink-0" data-id="element-195">
              <div className="flex gap-1.5 overflow-x-auto pb-1" data-id="element-196">
                {quickPrompts.map(p => <button key={p} onClick={() => sendQuestion(p)} disabled={thinking} className="shrink-0 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-full px-3 py-1.5 transition-colors" data-id="element-197">
                    {p}
                  </button>)}
              </div>
            </div>

            {/* Input */}
            <div className="px-5 py-3 border-t border-gray-100 bg-white shrink-0 flex items-center gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]" data-id="element-198">
              <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendQuestion(input)} placeholder="Ask the AI to relay something to the customer…" className="flex-1 px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" data-id="element-199" />
              <button onClick={() => sendQuestion(input)} disabled={!input.trim() || thinking} className="p-2.5 rounded-xl bg-gradient-to-br from-brand-400 to-purple-500 hover:from-brand-300 hover:to-purple-400 disabled:from-gray-200 disabled:to-gray-200 disabled:cursor-not-allowed text-white transition-all shadow" aria-label="Send" data-id="element-200">
                <Send className="w-4 h-4" data-id="element-201" />
              </button>
            </div>
          </motion.aside>
        </>}
    </AnimatePresence>;
}